import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram, SlidersHorizontal, GripVertical } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScrollReveal } from '../components/ScrollReveal';
import { NextPageLink } from '../components/NextPageLink';
import { useSEO } from '../utils/seo';
type GalleryItem = {
  image?: string;
  beforeImage?: string;
  afterImage?: string;
  caption: string;
  split: 'vertical' | 'horizontal';
};
const DragRevealSlider = ({
  item
}: {
  item: GalleryItem;
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const hasSeparateImages = item.beforeImage && item.afterImage;
  const beforeStyle = item.split === 'vertical' ? {
    objectPosition: 'center top',
    width: '200%',
    height: '100%',
    maxWidth: 'none',
    transform: 'translateX(-25%)'
  } : {
    objectPosition: 'left center',
    width: '100%',
    height: '200%',
    maxWidth: 'none',
    transform: 'translateY(-0%)'
  };
  const afterStyle = item.split === 'vertical' ? {
    objectPosition: 'center bottom',
    width: '200%',
    height: '100%',
    maxWidth: 'none',
    transform: 'translateX(-25%)'
  } : {
    objectPosition: 'right center',
    width: '100%',
    height: '200%',
    maxWidth: 'none',
    transform: 'translateY(-50%)'
  };
  const separateImageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    objectPosition: 'center'
  };
  return <div className="bg-brand-darker rounded-2xl overflow-hidden border border-brand-border/30 group" data-id="element-216">
      <div className="relative aspect-[3/4] w-full overflow-hidden select-none" data-id="element-217">
        {/* After Image (Base) */}
        <div className="absolute inset-0 w-full h-full overflow-hidden" data-id="element-218">
          <img src={hasSeparateImages ? item.afterImage : item.image} alt="Après" className="absolute top-0 left-0" style={hasSeparateImages ? separateImageStyle : afterStyle} draggable={false} data-id="element-219" />
        </div>

        {/* Before Image (Overlay with clip-path) */}
        <div className="absolute inset-0 w-full h-full overflow-hidden" style={{
        clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
      }} data-id="element-220">
          <img src={hasSeparateImages ? item.beforeImage : item.image} alt="Avant" className="absolute top-0 left-0" style={hasSeparateImages ? separateImageStyle : beforeStyle} draggable={false} data-id="element-221" />
        </div>

        {/* Handle */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-brand-light z-20 pointer-events-none" style={{
        left: `${sliderPosition}%`
      }} data-id="element-222">
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-brand-darker border border-brand-border flex items-center justify-center shadow-lg" data-id="element-223">
            <GripVertical size={16} className="text-brand-light" data-id="element-224" />
          </div>
        </div>

        {/* Invisible Range Input */}
        <input type="range" min="0" max="100" value={sliderPosition} onChange={e => setSliderPosition(Number(e.target.value))} className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30 m-0" data-id="element-225" />

        {/* Badges */}
        <div className="absolute top-3 md:top-4 left-3 md:left-4 z-10 bg-brand-dark/80 backdrop-blur-sm px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold text-brand-light uppercase tracking-wider pointer-events-none" data-id="element-226">
          Avant
        </div>
        <div className="absolute top-3 md:top-4 right-3 md:right-4 z-10 bg-brand-dark/80 backdrop-blur-sm px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold text-brand-light uppercase tracking-wider pointer-events-none" data-id="element-227">
          Après
        </div>
      </div>
      <div className="p-4 md:p-6 text-center border-t border-brand-border/30" data-id="element-228">
        <p className="text-brand-light text-sm md:text-base font-medium tracking-wide" data-id="element-229">
          {item.caption}
        </p>
      </div>
    </div>;
};
export const AvantApres = () => {
  useSEO({
    title: 'Résultats Avant / Après | Détatouage Laser Martigny | Dispel Your Tattoo',
    description: 'Découvrez les résultats réels de détatouage laser chez Dispel Your Tattoo à Martigny. Photos avant/après de traitements sur différents types de tatouages et de peaux.',
    canonical: '/avant-apres'
  });
  const galleryItems: GalleryItem[] = [{
    beforeImage: 'https://cdn.magicpatterns.com/uploads/1NM1EVjutppXAPJaAFXDQK/dispel_before_001.webp',
    afterImage: 'https://cdn.magicpatterns.com/uploads/6aB4TsApQ2isjr28xnCah8/dispel_after_001.webp',
    caption: 'Mandala et lettrage main — retrait complet',
    split: 'vertical' as const
  }, {
    beforeImage: 'https://cdn.magicpatterns.com/uploads/te8S7XKPhNtJ4tvksUJts4/dispel_before_002.webp',
    afterImage: 'https://cdn.magicpatterns.com/uploads/grAKKg6GHxtkRYfLmJ2T2X/dispel_after_002.webp',
    caption: 'Tatouage line-art bras — retrait complet',
    split: 'vertical' as const
  }, {
    beforeImage: 'https://cdn.magicpatterns.com/uploads/rUjij69NUwwdaA1GQGrRAj/dispel_before_003.webp',
    afterImage: 'https://cdn.magicpatterns.com/uploads/bq7SjpkVPiYty3kgFw1cwa/dispel_after_003.webp',
    caption: 'Lettrage haut du dos — atténuation en cours',
    split: 'vertical' as const
  }, {
    beforeImage: 'https://cdn.magicpatterns.com/uploads/6TPq4XQ6m1zXivQyi8cK5z/dispel_before_004.webp',
    afterImage: 'https://cdn.magicpatterns.com/uploads/vVbvVKau53gVAc1xHkF88Z/dispel_after_004.webp',
    caption: 'Pivoine et hirondelle nuque — retrait quasi complet',
    split: 'vertical' as const
  }, {
    beforeImage: 'https://cdn.magicpatterns.com/uploads/rtq2XE7YWasM7UTieWmKrD/dispel_before_005.webp',
    afterImage: 'https://cdn.magicpatterns.com/uploads/jbpWBUsivZmu272XbHfqXu/dispel_after_005.webp',
    caption: 'Cœur prénoms haut du dos — atténuation en cours',
    split: 'vertical' as const
  }, {
    beforeImage: 'https://cdn.magicpatterns.com/uploads/5JEVr9haHJhk3EFgaKKNXP/dispel_before_006.jpg',
    afterImage: 'https://cdn.magicpatterns.com/uploads/rEi1fT5GdauA6FZV6XfvPo/dispel_after_006.jpg',
    caption: 'Serpent et lune flanc — retrait quasi complet',
    split: 'vertical' as const
  }];
  const testimonials = [{
    quote: 'Après 5 séances, mon tatouage au bras a presque entièrement disparu. Le processus a été bien expliqué dès le départ.',
    author: 'Marc, Sion'
  }, {
    quote: "Je voulais éclaircir mon tatouage pour un cover-up. Le résultat est exactement ce que j'espérais.",
    author: 'Laura, Monthey'
  }, {
    quote: "Professionnel, transparent et efficace. Les photos parlent d'elles-mêmes.",
    author: 'Thomas, Martigny'
  }];
  const [progressionStep, setProgressionStep] = useState(0);
  const progressionImages = [{
    stage: 0,
    label: 'Avant',
    image: 'https://cdn.magicpatterns.com/uploads/hDZN9tnr22239VqoZ7QDib/session0.webp'
  }, {
    stage: 1,
    label: 'Séance 1',
    image: 'https://cdn.magicpatterns.com/uploads/nmWrtcJvDAdeehVQLKwLqW/session1.webp'
  }, {
    stage: 2,
    label: 'Séance 2',
    image: 'https://cdn.magicpatterns.com/uploads/1YsT83k2zkEE1ENP8wgcBd/session2.webp'
  }, {
    stage: 3,
    label: 'Séance 3',
    image: 'https://cdn.magicpatterns.com/uploads/cESUBiKt81EvoZCHdAhseT/session3.webp'
  }, {
    stage: 4,
    label: 'Séance 4',
    image: 'https://cdn.magicpatterns.com/uploads/7hztLUx8N8FEzKHp28KoYw/session4.webp'
  }, {
    stage: 5,
    label: 'Séance 5',
    image: 'https://cdn.magicpatterns.com/uploads/e3UYVN62K5ZmhVGqY48usW/session5.webp'
  }, {
    stage: 6,
    label: 'Séance 6',
    image: 'https://cdn.magicpatterns.com/uploads/3hRmJ87E6EjFx5sPiBuLyz/session6.webp'
  }, {
    stage: 7,
    label: 'Séance 7',
    image: 'https://cdn.magicpatterns.com/uploads/2SVHqwGimMUgD5qQXRXwXv/session7.webp'
  }];
  return <main className="pt-32 pb-0 bg-brand-dark" data-id="element-230">
      {/* Header Section */}
      <section className="py-16 md:py-24 bg-brand-darker text-center px-6 md:px-12" data-id="element-231">
        <div className="max-w-[1000px] mx-auto" data-id="element-232">
          <ScrollReveal data-id="element-233">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-medium text-brand-light mb-6 md:mb-8 tracking-tight text-balance" data-id="element-234">
              Résultats de Détatouage Avant - Après
            </h1>
          </ScrollReveal>
          {/* ... existing code ... */}
        </div>
      </section>

      {/* Progression Slider Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-brand-dark px-6 md:px-12" data-id="element-235">
        <div className="max-w-[1000px] mx-auto" data-id="element-236">
          <ScrollReveal data-id="element-237">
            <div className="text-center mb-12 md:mb-16" data-id="element-238">
              <h2 className="text-2xl md:text-3xl lg:text-5xl font-medium text-brand-light mb-4 md:mb-6 tracking-tight" data-id="element-239">
                Évolution du traitement
              </h2>
              <p className="text-base md:text-lg text-brand-gray font-light max-w-2xl mx-auto" data-id="element-240">
                Observez la disparition progressive de l'encre au fil des
                séances. Utilisez le curseur ou balayez l'image pour naviguer à
                travers les différentes étapes du détatouage.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-241">
            <div className="bg-brand-darker rounded-3xl p-6 md:p-10 border border-brand-border/50" data-id="element-242">
              {/* Image Container */}
              <motion.div className="relative w-full max-w-md mx-auto aspect-[3/4] rounded-2xl overflow-hidden bg-brand-dark mb-8 md:mb-12 cursor-grab active:cursor-grabbing" drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.2} onDragEnd={(e, {
              offset
            }) => {
              const swipeThreshold = 50;
              if (offset.x < -swipeThreshold) {
                if (progressionStep < progressionImages.length - 1) {
                  setProgressionStep(prev => prev + 1);
                }
              } else if (offset.x > swipeThreshold) {
                if (progressionStep > 0) {
                  setProgressionStep(prev => prev - 1);
                }
              }
            }} data-id="element-243">
                {progressionImages.map((img, idx) => <img key={idx} src={img.image} alt={img.label} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out pointer-events-none ${progressionStep === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-244" />)}

                {/* Step Badge */}
                <div className="absolute top-4 md:top-6 left-4 md:left-6 z-20 bg-brand-dark/90 backdrop-blur-md px-4 py-2 rounded-full border border-brand-border/50 pointer-events-none" data-id="element-245">
                  <span className="text-sm md:text-base font-semibold text-brand-light uppercase tracking-widest" data-id="element-246">
                    {progressionImages[progressionStep].label}
                  </span>
                </div>
              </motion.div>

              {/* Slider Controls */}
              <div className="max-w-2xl mx-auto" data-id="element-247">
                <div className="flex items-center justify-between mb-4 px-2" data-id="element-248">
                  <span className="text-xs md:text-sm font-medium text-brand-gray uppercase tracking-wider" data-id="element-249">
                    Avant
                  </span>
                  <span className="text-xs md:text-sm font-medium text-brand-gray uppercase tracking-wider" data-id="element-250">
                    Fin
                  </span>
                </div>

                <div className="relative flex items-center" data-id="element-251">
                  <SlidersHorizontal className="absolute left-0 w-5 h-5 text-brand-gray/50 pointer-events-none hidden md:block -ml-8" data-id="element-252" />
                  <input type="range" min="0" max="7" step="1" value={progressionStep} onChange={e => setProgressionStep(parseInt(e.target.value))} className="w-full h-2 bg-brand-dark rounded-lg appearance-none cursor-pointer accent-brand-light" style={{
                  background: `linear-gradient(to right, #f5f5f0 ${progressionStep / 7 * 100}%, #111111 ${progressionStep / 7 * 100}%)`
                }} data-id="element-253" />
                </div>

                {/* Step Indicators */}
                <div className="flex justify-between mt-6 px-1" data-id="element-254">
                  {progressionImages.map((step, idx) => <button key={idx} onClick={() => setProgressionStep(idx)} className={`flex flex-col items-center space-y-2 group ${progressionStep === idx ? 'opacity-100' : 'opacity-50 hover:opacity-80'} transition-opacity`} data-id="element-255">
                      <div className={`w-3 h-3 rounded-full transition-colors ${progressionStep >= idx ? 'bg-brand-light' : 'bg-brand-border'}`} data-id="element-256" />
                      <span className="text-[10px] md:text-xs font-medium text-brand-gray hidden md:block uppercase tracking-wider" data-id="element-257">
                        {idx === 0 ? '0' : idx}
                      </span>
                    </button>)}
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 md:py-24 lg:py-32 px-6 md:px-12 max-w-[1400px] mx-auto border-t border-brand-border/30" data-id="element-258">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10" data-id="element-259">
          {galleryItems.map((item, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-260">
              <DragRevealSlider item={item} data-id="element-261" />
            </ScrollReveal>)}
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-brand-darker px-6 md:px-12 border-t border-brand-border/30" data-id="element-262">
        <div className="max-w-[1400px] mx-auto" data-id="element-263">
          <ScrollReveal data-id="element-264">
            <h2 className="text-2xl md:text-3xl lg:text-5xl font-medium text-brand-light mb-10 md:mb-16 text-center tracking-tight" data-id="element-265">
              Ce que disent nos clients
            </h2>
          </ScrollReveal>
          {/* ... existing code ... */}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 lg:py-32 border-t border-brand-border/30 text-center px-6 md:px-12" data-id="element-266">
        <div className="max-w-3xl mx-auto" data-id="element-267">
          <ScrollReveal data-id="element-268">
            <h2 className="text-2xl md:text-3xl lg:text-5xl font-medium text-brand-light mb-4 md:mb-6 tracking-tight" data-id="element-269">
              Prêt à voir vos propres résultats ?
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1} data-id="element-270">
            <p className="text-base md:text-lg lg:text-xl text-brand-gray font-light mb-8 md:mb-12 leading-relaxed" data-id="element-271">
              Chaque tatouage est unique. Prenez rendez-vous pour une
              consultation personnalisée et découvrez ce que nous pouvons
              accomplir ensemble.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.2} data-id="element-272">
            <Link to="/contact#consultation-form" className="inline-flex items-center space-x-2 px-8 md:px-10 py-4 md:py-5 bg-brand-light text-brand-dark rounded-full font-bold uppercase tracking-wider hover:bg-white transition-colors duration-300 text-sm md:text-base" data-id="element-273">
              <span data-id="element-274">Réserver une consultation</span>
              <ArrowRight size={16} className="md:w-[18px] md:h-[18px]" data-id="element-275" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
      <NextPageLink data-id="element-276" />
    </main>;
};